import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import { format } from 'date-fns';
import { DatePickerField } from '../DatePickerField';
import { SubscriptionPlanDropdown } from '@/epics/offerManagment/components/SubscriptionPlanDropdown';
import { FilterPopupProps } from './types';

/**
 * Generic filter popup shell — Apply/Clear buttons, date range, status/plan slots.
 *
 * @see docs/DEVELOPMENT_GUIDELINES.md — "Filter Popup: Pending vs Applied State"
 * Callers must maintain separate pending and applied state. Never pass the same
 * array to both the dropdown and the API call.
 */
export function FilterPopup({
  isOpen,
  onClose,
  billingCycleOptions,
  onBillingCycleChange,
  dateRange,
  onDateRangeChange,
  statusOptions = [],
  onStatusChange,
  availabilityOptions,
  onAvailabilityChange,
  subscriptionPlanOptions,
  onSubscriptionPlanChange,
  showSubscriptionPlanSection = false,
  subscriptionPlansLoading = false,
  usePlanDropdown = false,
  showDateRange = true,
  allowFutureDates = false,
  dropdown,
  dropdownHasValue = false,
  className,
  onApply,
  onClear,
}: FilterPopupProps) {
  const [dateError, setDateError] = useState('');
  const [planSearch, setPlanSearch] = useState('');
  const [popupMaxHeight, setPopupMaxHeight] = useState<number>(600);
  // null = full-scroll mode (tight viewport); string = status-only scroll mode
  const [statusListMaxHeight, setStatusListMaxHeight] = useState<string | null>(
    '208px'
  );
  const [fixedPos, setFixedPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const startDateRef = useRef<HTMLDivElement>(null);
  const endDateRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const dateSectionRef = useRef<HTMLDivElement>(null);
  const dropdownSectionRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const useBillingCycle = Boolean(billingCycleOptions?.length);
  const useSubscriptionPlan =
    showSubscriptionPlanSection ||
    Boolean(subscriptionPlanOptions?.length && onSubscriptionPlanChange);

  const subscriptionPlanSelectedIdsMemo = useMemo(
    () =>
      (subscriptionPlanOptions ?? []).filter((p) => p.checked).map((p) => p.id),
    [subscriptionPlanOptions]
  );
  const onSubscriptionPlanSelectionChange = useCallback(
    (ids: string[]) => {
      if (!subscriptionPlanOptions || !onSubscriptionPlanChange) {
        return;
      }
      const updated = subscriptionPlanOptions.map((opt) => ({
        ...opt,
        checked: ids.includes(opt.id),
      }));
      onSubscriptionPlanChange(updated);
    },
    [subscriptionPlanOptions, onSubscriptionPlanChange]
  );
  const useAvailability = Boolean(
    availabilityOptions?.length && onAvailabilityChange
  );
  const showDateRangeSection = !useBillingCycle && showDateRange;

  // For "created"-style filters dates are capped at today; for scheduled-date
  // filters (allowFutureDates) the cap is lifted and the year picker extends
  // a few years ahead so upcoming visits can be selected.
  const dateMax = allowFutureDates ? undefined : new Date();
  const dateEndYear = new Date().getFullYear() + (allowFutureDates ? 5 : 0);

  // Calculate smart placement for datepickers to avoid overflow

  const recalcStatusHeight = useCallback(() => {
    if (!modalRef.current) {
      return;
    }
    const rect = modalRef.current.getBoundingClientRect();
    setFixedPos({ top: rect.top, right: window.innerWidth - rect.right });
    const available = Math.max(200, window.innerHeight - rect.top - 16);
    setPopupMaxHeight(available);
    const dateH = dateSectionRef.current?.offsetHeight ?? 0;
    const dropdownH = dropdownSectionRef.current?.offsetHeight ?? 0;
    const footerH = footerRef.current?.offsetHeight ?? 0;
    // overhead: popup padding (40) + dropdown section + gap (32) + date section + gap (32) + status header+gap (32)
    const overhead =
      40 +
      (dropdownH > 0 ? dropdownH + 32 : 0) +
      (dateH > 0 ? dateH + 32 : 0) +
      32;
    const spaceForStatus = available - footerH - overhead;
    if (spaceForStatus >= 80) {
      // Enough room: dates visible, only status list scrolls
      setStatusListMaxHeight(`${Math.min(208, spaceForStatus)}px`);
    } else {
      // Tight viewport (150%+): whole content scrolls
      setStatusListMaxHeight(null);
    }
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    recalcStatusHeight();
  }, [isOpen, showDateRangeSection, recalcStatusHeight]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener('resize', recalcStatusHeight);
    return () => {
      window.removeEventListener('resize', recalcStatusHeight);
      setFixedPos(null);
    };
  }, [isOpen, recalcStatusHeight]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus({ preventScroll: true });
      setPlanSearch('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const hasFilters =
    dropdownHasValue ||
    (useBillingCycle
      ? (billingCycleOptions?.some((opt) => opt.checked) ?? false) ||
        statusOptions.some((opt) => opt.checked)
      : (showDateRangeSection &&
          (dateRange?.startDate || dateRange?.endDate)) ||
        statusOptions.some((opt) => opt.checked)) ||
    (useAvailability &&
      (availabilityOptions?.some((opt) => opt.checked) ?? false)) ||
    (useSubscriptionPlan &&
      (subscriptionPlanOptions?.some((opt) => opt.checked) ?? false));

  const handleBillingCycleToggle = (index: number) => {
    if (!billingCycleOptions) {
      return;
    }
    const updated = billingCycleOptions.map((opt, i) =>
      i === index ? { ...opt, checked: !opt.checked } : opt
    );
    onBillingCycleChange?.(updated);
  };

  const handleStatusToggle = (index: number) => {
    const updated = statusOptions.map((opt, i) =>
      i === index ? { ...opt, checked: !opt.checked } : opt
    );
    onStatusChange?.(updated);
  };

  const handleAvailabilityToggle = (index: number) => {
    if (!availabilityOptions || !onAvailabilityChange) {
      return;
    }
    const updated = availabilityOptions.map((opt, i) =>
      i === index ? { ...opt, checked: !opt.checked } : opt
    );
    onAvailabilityChange(updated);
  };

  const handlePlanToggle = (id: string) => {
    if (!subscriptionPlanOptions || !onSubscriptionPlanChange) {
      return;
    }
    const updated = subscriptionPlanOptions.map((opt) =>
      opt.id === id ? { ...opt, checked: !opt.checked } : opt
    );
    onSubscriptionPlanChange(updated);
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    const newRange = { ...dateRange, [field]: value };

    if (
      newRange.startDate &&
      newRange.endDate &&
      newRange.startDate > newRange.endDate
    ) {
      setDateError('Data początkowa nie może być późniejsza niż data końcowa.');
    } else {
      setDateError('');
    }

    onDateRangeChange?.(newRange);
  };

  const handleStartDateSelect = (date: Date | undefined) => {
    const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
    handleDateChange('startDate', dateStr);
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
    handleDateChange('endDate', dateStr);
  };

  const handleClose = () => {
    setDateError('');
    onClose();
  };

  const handleClear = () => {
    setDateError('');
    onClear();
  };

  // Handle ESC and Tab keys: close when leaving first/last focusable (no focus trap)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
      return;
    }

    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [role="button"]:not([disabled]), [tabindex="0"]'
      );

      if (!focusableElements || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab from first focusable: close popup and let focus move backward
        if (document.activeElement === firstElement) {
          handleClose();
        }
      } else {
        // Tab from last focusable: close popup and let focus move forward
        if (document.activeElement === lastElement) {
          handleClose();
        }
      }
    }
  };

  return (
    <>
      <style>{`
        .dropdown {
          position: relative;
        }

        .dropdown-content {
          position: absolute;
          background-color: #f9f9f9;
          min-width: 130px;
          box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
          padding: 12px 16px;
        }

        .filter-scroll-area {
          scrollbar-width: thin;
          scrollbar-color: #C0C4C8 transparent;
        }
        .filter-scroll-area::-webkit-scrollbar {
          width: 5px;
          display: block;
        }
        .filter-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }
        .filter-scroll-area::-webkit-scrollbar-thumb {
          background-color: #C0C4C8;
          border-radius: 3px;
        }
        .filter-scroll-area::-webkit-scrollbar-thumb:hover {
          background-color: #9EA3A8;
        }

      `}</style>
      <div className="fixed inset-0 z-40" onClick={handleClose} />
      <div
        ref={modalRef}
        className={`filter-popup-content dropdown-content z-50 w-[296px] bg-[#FAFAFA] rounded-xl flex flex-col${className ? ` ${className}` : ''}`}
        style={{
          position: fixedPos ? 'fixed' : 'absolute',
          top: fixedPos ? fixedPos.top : '3rem',
          right: fixedPos ? fixedPos.right : 0,
          maxHeight: fixedPos ? popupMaxHeight : undefined,
          overflow: fixedPos
            ? dropdown && statusListMaxHeight !== null
              ? 'visible'
              : 'hidden'
            : undefined,
          padding: '20px',
          boxShadow:
            '0px 2px 3px 0px rgba(0, 0, 0, 0.15), 0px 6px 10px 4px rgba(0, 0, 0, 0.08), 0px -2px 4px 0px rgba(0, 0, 0, 0.15)',
        }}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div
          className={`filter-scroll-area main-container flex-1 min-h-0 w-full flex flex-col gap-[32px] pb-6${dropdown && statusListMaxHeight !== null ? ' overflow-visible' : statusListMaxHeight === null ? ' overflow-y-auto' : ' overflow-hidden'}`}
        >
          {dropdown && (
            <div
              ref={dropdownSectionRef}
              className="w-full flex flex-col gap-3"
            >
              {dropdown}
            </div>
          )}
          {useSubscriptionPlan && (
            <div className="w-full flex flex-col gap-3">
              <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                Nazwa planu
              </div>
              {subscriptionPlansLoading ? (
                <div
                  className="w-full h-10 rounded bg-[#E5E7E9] animate-pulse"
                  data-testid="subscription-plan-loading-shimmer"
                />
              ) : usePlanDropdown ? (
                <SubscriptionPlanDropdown
                  selectedIds={subscriptionPlanSelectedIdsMemo}
                  onSelectionChange={onSubscriptionPlanSelectionChange}
                  label="Nazwa planu"
                  toolTip={true}
                />
              ) : (
                <>
                  <input
                    type="text"
                    value={planSearch}
                    onChange={(e) => setPlanSearch(e.target.value)}
                    placeholder="Szukaj..."
                    maxLength={200}
                    className="w-full h-10 px-3 rounded border border-[#DCDDE0] text-[#181A1B] text-sm leading-5 tracking-[0.25px] placeholder:text-[#878B8E] placeholder:text-[14px] placeholder:font-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    aria-label="Szukaj planu"
                  />
                  <div className="plan-list max-h-[160px] overflow-y-auto flex flex-col gap-0.5 pr-1">
                    {(subscriptionPlanOptions ?? [])
                      .filter(
                        (p) =>
                          !planSearch.trim() ||
                          p.name
                            .toLowerCase()
                            .includes(planSearch.trim().toLowerCase())
                      )
                      .map((plan) => (
                        <label
                          key={plan.id}
                          className="filter-option-label h-10 flex items-center gap-1 w-full cursor-pointer rounded-lg outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#181A1B] has-[:focus-visible]:ring-offset-2"
                        >
                          <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#181A1B]/8 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={plan.checked}
                              onChange={() => handlePlanToggle(plan.id)}
                              className="sr-only"
                              data-testid={`filter-plan-${plan.id}`}
                            />
                            <div
                              className={`w-[18px] h-[18px] rounded-[2px] border-2 flex items-center justify-center cursor-pointer ${
                                plan.checked
                                  ? 'bg-[#2A4455] border-[#2A4455]'
                                  : 'bg-white border-[#474A4C]'
                              }`}
                            >
                              {plan.checked && (
                                <svg
                                  width="12"
                                  height="9"
                                  viewBox="0 0 12 9"
                                  fill="none"
                                >
                                  <path
                                    d="M1 4.5L4 7.5L11 1"
                                    stroke="#FFFFFF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                          <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-pointer whitespace-nowrap truncate max-w-[200px]">
                            {plan.name}
                          </span>
                        </label>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
          {showDateRangeSection && (
            <div ref={dateSectionRef} className="flex flex-col gap-3">
              <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                Zakres dat
              </div>
              <div className="w-full flex flex-col gap-3">
                <div ref={startDateRef} data-testid="filter-start-date-input">
                  <DatePickerField
                    label="Data początkowa"
                    value={
                      dateRange?.startDate
                        ? (() => {
                            try {
                              const d = new Date(dateRange.startDate);
                              return isNaN(d.getTime()) ? undefined : d;
                            } catch {
                              return undefined;
                            }
                          })()
                        : undefined
                    }
                    onChange={handleStartDateSelect}
                    error={!!dateError}
                    maxDate={dateMax}
                    startYear={2001}
                    endYear={dateEndYear}
                    placement={'left-start'}
                    compact
                  />
                </div>
                <div ref={endDateRef} data-testid="filter-end-date-input">
                  <DatePickerField
                    label="Data końcowa"
                    value={
                      dateRange?.endDate
                        ? (() => {
                            try {
                              const d = new Date(dateRange.endDate);
                              return isNaN(d.getTime()) ? undefined : d;
                            } catch {
                              return undefined;
                            }
                          })()
                        : undefined
                    }
                    onChange={handleEndDateSelect}
                    error={!!dateError}
                    errorMessage={dateError || undefined}
                    minDate={
                      dateRange?.startDate
                        ? (() => {
                            try {
                              const d = new Date(dateRange.startDate);
                              return isNaN(d.getTime()) ? undefined : d;
                            } catch {
                              return undefined;
                            }
                          })()
                        : undefined
                    }
                    maxDate={dateMax}
                    startYear={2001}
                    endYear={dateEndYear}
                    placement={'left-start'}
                    compact
                  />
                </div>
              </div>
            </div>
          )}
          {/* Status filter first */}
          {statusOptions.length > 0 && (
            <div className="w-full flex flex-col gap-1">
              <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                Status
              </div>
              <div
                className={`flex flex-col gap-0.5${statusListMaxHeight !== null ? ' filter-scroll-area overflow-y-auto' : ''}`}
                style={
                  statusListMaxHeight !== null
                    ? { maxHeight: statusListMaxHeight }
                    : undefined
                }
              >
                {statusOptions.map((option, index) => {
                  const checkboxRef = (_el: HTMLDivElement | null) => {};
                  return (
                    <label
                      key={option.value}
                      data-testid={`filter-status-${option.value}`}
                      className="filter-option-label h-10 flex items-center gap-1 w-full cursor-pointer rounded-lg outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#181A1B] has-[:focus-visible]:ring-offset-2"
                    >
                      <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#181A1B]/8 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={option.checked}
                          onChange={() => handleStatusToggle(index)}
                          className="sr-only"
                          data-testid={`filter-status-${option.value}-checkbox`}
                        />
                        <div
                          ref={checkboxRef}
                          className={`w-[18px] h-[18px] rounded-[2px] border-2 flex items-center justify-center cursor-pointer ${
                            option.checked
                              ? 'bg-[#2A4455] border-[#2A4455]'
                              : 'bg-white border-[#474A4C]'
                          }`}
                        >
                          {option.checked && (
                            <svg
                              width="12"
                              height="9"
                              viewBox="0 0 12 9"
                              fill="none"
                            >
                              <path
                                d="M1 4.5L4 7.5L11 1"
                                stroke="#FFFFFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-pointer whitespace-nowrap">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {useAvailability && availabilityOptions && (
            <div className="w-full flex flex-col gap-1">
              <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                Aktualna dostępność
              </div>
              <div className="flex flex-col gap-0.5">
                {availabilityOptions.map((option, index) => (
                  <label
                    key={option.value}
                    data-testid={`filter-availability-${option.value}`}
                    className="filter-option-label h-10 flex items-center gap-1 w-full cursor-pointer rounded-lg outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#181A1B] has-[:focus-visible]:ring-offset-2"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#181A1B]/8 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={option.checked}
                        onChange={() => handleAvailabilityToggle(index)}
                        className="sr-only"
                        data-testid={`filter-availability-${option.value}-checkbox`}
                      />
                      <div
                        className={`w-[18px] h-[18px] rounded-[2px] border-2 flex items-center justify-center cursor-pointer ${
                          option.checked
                            ? 'bg-[#2A4455] border-[#2A4455]'
                            : 'bg-white border-[#474A4C]'
                        }`}
                      >
                        {option.checked && (
                          <svg
                            width="12"
                            height="9"
                            viewBox="0 0 12 9"
                            fill="none"
                          >
                            <path
                              d="M1 4.5L4 7.5L11 1"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-pointer whitespace-nowrap">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {useBillingCycle && (
            <div className="w-full flex flex-col gap-1">
              <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                Cykl rozliczeniowy
              </div>
              <div className="flex flex-col gap-0.5">
                {(billingCycleOptions ?? []).map((option, index) => (
                  <label
                    key={option.value}
                    data-testid={`filter-billing-cycle-${option.value.toLowerCase()}`}
                    className="filter-option-label h-10 flex items-center gap-0.5 w-full cursor-pointer rounded-lg outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#181A1B] has-[:focus-visible]:ring-offset-2"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#181A1B]/8 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={option.checked}
                        onChange={() => handleBillingCycleToggle(index)}
                        className="sr-only"
                        data-testid={`filter-billing-cycle-${option.value.toLowerCase()}-checkbox`}
                      />
                      <div
                        className={`w-[18px] h-[18px] rounded-[2px] border-2 flex items-center justify-center cursor-pointer ${
                          option.checked
                            ? 'bg-[#2A4455] border-[#2A4455]'
                            : 'bg-white border-[#474A4C]'
                        }`}
                      >
                        {option.checked && (
                          <svg
                            width="12"
                            height="9"
                            viewBox="0 0 12 9"
                            fill="none"
                          >
                            <path
                              d="M1 4.5L4 7.5L11 1"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-pointer whitespace-nowrap">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          ref={footerRef}
          className="footer-container shrink-0 w-full flex flex-col gap-3"
          data-dropdown-boundary
        >
          <div className="h-px bg-[#DCDDE0]" />
          <div className="w-full h-12 flex justify-end items-center gap-3">
            <button
              onClick={handleClear}
              disabled={!hasFilters}
              data-testid="filter-clear-button"
              className="footer-button w-[151px] h-10 flex items-center justify-center px-4 py-2.5 gap-2 rounded-lg border border-[#DCDDE0] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#181A1B]"
            >
              <span className="text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                Wyczyść wszystko
              </span>
            </button>
            <button
              onClick={onApply}
              disabled={(showDateRangeSection && !!dateError) || !hasFilters}
              data-testid="filter-apply-button"
              className="footer-button w-[86px] h-10 flex items-center justify-center bg-[#2A4455] rounded-lg hover:bg-[#1f3340] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#181A1B]"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                Zastosuj
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
